/**
 * All bakery-facing dates are reckoned in the shop's timezone, not the device's.
 * A rider opening the app on a phone still set to UTC must see the same
 * "today" as the register on the counter.
 */
export const SHOP_TIMEZONE = 'Asia/Manila';
const isoDateFormatter = new Map();
function dateFormatter(timeZone) {
    let formatter = isoDateFormatter.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        isoDateFormatter.set(timeZone, formatter);
    }
    return formatter;
}
/** The calendar date in the shop's timezone, as YYYY-MM-DD. */
export function shopDate(instant = new Date(), timeZone = SHOP_TIMEZONE) {
    const date = typeof instant === 'string' ? new Date(instant) : instant;
    // en-CA formats as YYYY-MM-DD, which is exactly the shape we want.
    return dateFormatter(timeZone).format(date);
}
export function addDays(isoDate, days) {
    const [year, month, day] = isoDate.split('-').map(Number);
    const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}
export function daysBetween(fromIso, toIso) {
    const from = Date.parse(`${fromIso}T00:00:00Z`);
    const to = Date.parse(`${toIso}T00:00:00Z`);
    return Math.round((to - from) / 86_400_000);
}
/** Weekday index in the shop's timezone. 0 = Sunday. */
export function shopWeekday(instant = new Date(), timeZone = SHOP_TIMEZONE) {
    const iso = shopDate(instant, timeZone);
    return new Date(`${iso}T00:00:00Z`).getUTCDay();
}
/**
 * Which tab an order belongs on. `dueAt` is a full timestamp; the bucket is
 * decided by its calendar date in the shop's timezone, except that a dated
 * order whose moment has already passed is overdue regardless of the day.
 */
export function dueBucketFor(dueAt, now = new Date(), timeZone = SHOP_TIMEZONE) {
    if (!dueAt)
        return 'undated';
    const today = shopDate(now, timeZone);
    const due = shopDate(dueAt, timeZone);
    if (due < today)
        return 'overdue';
    if (due === today) {
        return Date.parse(dueAt) < now.getTime() ? 'overdue' : 'today';
    }
    if (due === addDays(today, 1))
        return 'tomorrow';
    return 'advance';
}
export const DUE_BUCKET_LABELS = {
    overdue: 'Overdue',
    today: 'Today',
    tomorrow: 'Tomorrow',
    advance: 'Advance',
    undated: 'No date',
};
/** "in 2 hours", "3 days ago". Used on the orders board and in the inbox. */
export function relativeTime(instant, now = new Date()) {
    const target = typeof instant === 'string' ? Date.parse(instant) : instant.getTime();
    const diffSeconds = Math.round((target - now.getTime()) / 1000);
    const absolute = Math.abs(diffSeconds);
    const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    if (absolute < 45)
        return 'just now';
    if (absolute < 3_600)
        return formatter.format(Math.round(diffSeconds / 60), 'minute');
    if (absolute < 86_400)
        return formatter.format(Math.round(diffSeconds / 3_600), 'hour');
    if (absolute < 604_800)
        return formatter.format(Math.round(diffSeconds / 86_400), 'day');
    if (absolute < 2_629_800)
        return formatter.format(Math.round(diffSeconds / 604_800), 'week');
    if (absolute < 31_557_600)
        return formatter.format(Math.round(diffSeconds / 2_629_800), 'month');
    return formatter.format(Math.round(diffSeconds / 31_557_600), 'year');
}
/** Short shop-local time, e.g. "3:45 PM". */
export function formatShopTime(instant, timeZone = SHOP_TIMEZONE) {
    const date = typeof instant === 'string' ? new Date(instant) : instant;
    return new Intl.DateTimeFormat('en-PH', {
        timeZone,
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
}
/**
 * How long a rider is out with an order, once they set off.
 *
 * The shop picks a slot and delivers across the couple of hours after it. Told
 * "5:00 PM", customers stood at the door at five and rang to ask where the
 * bread was; told the window, they do not.
 */
export const DELIVERY_WINDOW_HOURS = 2;
/**
 * A delivery slot written as the window it really is — "5:00 – 7:00 PM".
 *
 * The meridiem is dropped from the first time when both halves share one,
 * because "5:00 PM – 7:00 PM" is the sort of thing a person reads twice. It
 * stays when they differ, where it is the whole point: "11:00 AM – 1:00 PM".
 */
export function formatDeliveryWindow(instant, hours = DELIVERY_WINDOW_HOURS, timeZone = SHOP_TIMEZONE) {
    const start = typeof instant === 'string' ? new Date(instant) : instant;
    const end = new Date(start.getTime() + hours * 60 * 60 * 1000);
    const from = formatShopTime(start, timeZone);
    const to = formatShopTime(end, timeZone);
    const meridiem = /\s?([AP]M)$/i;
    const fromPart = from.match(meridiem)?.[1];
    const toPart = to.match(meridiem)?.[1];
    return fromPart && fromPart === toPart ? `${from.replace(meridiem, '')} – ${to}` : `${from} – ${to}`;
}
/** "Sat, 30 Aug" — dense enough for list rows. */
export function formatShopDate(instant, timeZone = SHOP_TIMEZONE, options = { weekday: 'short', day: 'numeric', month: 'short' }) {
    const date = typeof instant === 'string' ? new Date(instant) : instant;
    return new Intl.DateTimeFormat('en-PH', { timeZone, ...options }).format(date);
}
/** Start-of-day and end-of-day instants for a shop calendar date. */
export function dayRange(isoDate, timeZone = SHOP_TIMEZONE) {
    // Manila has had no DST since 1978, so a fixed +08:00 offset is exact here and
    // avoids pulling in a full timezone library for the common case.
    const offset = timeZone === SHOP_TIMEZONE ? '+08:00' : 'Z';
    return {
        from: new Date(`${isoDate}T00:00:00${offset}`).toISOString(),
        to: new Date(`${addDays(isoDate, 1)}T00:00:00${offset}`).toISOString(),
    };
}
/**
 * The span between two shop dates, written the shortest way that stays clear.
 *
 * A roster paged a week at a time used to show nothing but seven day numbers,
 * and "Mon 29" reads the same in September as it does in March — so there was
 * no way to tell which week was on screen, or that one had crossed into a new
 * month. Naming the span fixes that.
 *
 * Philippine English puts the month first and the year behind a comma, so the
 * parts are assembled here rather than letting the locale lay out each end on
 * its own — formatting both ends whole would give "5 – October 11, 2026".
 * What is repeated is dropped, and nothing the reader needs is:
 *
 *   October 5 – 11, 2026
 *   September 29 – October 5, 2026
 *   December 28, 2026 – January 3, 2027
 */
export function formatDateSpan(from, to) {
    const start = new Date(`${from}T00:00:00Z`);
    const end = new Date(`${to}T00:00:00Z`);
    /*
     * Both ends are plain calendar dates pinned to midnight UTC, so they are read
     * back in UTC rather than shop time. Shifting them into Manila would move the
     * label a day against the dates it was handed.
     */
    const monthOf = (date) => new Intl.DateTimeFormat('en-PH', { month: 'long', timeZone: 'UTC' }).format(date);
    const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
    const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();
    const tail = `${monthOf(end)} ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
    // A span of one day is a day, not a range from itself to itself.
    if (from === to)
        return tail;
    if (sameMonth)
        return `${monthOf(start)} ${start.getUTCDate()} – ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
    if (sameYear)
        return `${monthOf(start)} ${start.getUTCDate()} – ${tail}`;
    return `${monthOf(start)} ${start.getUTCDate()}, ${start.getUTCFullYear()} – ${tail}`;
}
//# sourceMappingURL=dates.js.map